import { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { ExerciseTopSetEntry } from "./muscleUtils";
import { EXERCISE_COLORS } from "./TonnageChart";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

type TopSetChartProps = {
  weeksColumns: string[];
  entries: ExerciseTopSetEntry[];
  exerciseColorMap?: Record<string, string>;
  hasSelection?: boolean;
};

export const TopSetChart: React.FC<TopSetChartProps> = ({
  weeksColumns,
  entries,
  exerciseColorMap = {},
  hasSelection = false,
}) => {
  const isEmpty = entries.length === 0;

  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      const maxA = a.oneRMPerWeek.reduce<number>((s, v) => Math.max(s, v ?? 0), 0);
      const maxB = b.oneRMPerWeek.reduce<number>((s, v) => Math.max(s, v ?? 0), 0);
      return maxB - maxA;
    });
  }, [entries]);

  const data = {
    labels: weeksColumns,
    datasets: isEmpty
      ? []
      : sortedEntries.map((entry, idx) => {
          // Usa lo stesso colore del tonnellaggio se presente, altrimenti palette
          const color = exerciseColorMap[entry.exerciseId]
            || EXERCISE_COLORS[idx % EXERCISE_COLORS.length];
          const nullMask = entry.oneRMPerWeek.map((v) => v === null);
          const chartData = entry.oneRMPerWeek.map((v) => v ?? 0);
          return {
            label: entry.exerciseName,
            data: chartData,
            borderColor: color,
            backgroundColor: color,
            pointBackgroundColor: nullMask.map((isNull) =>
              isNull ? "#6B7280" : color
            ),
            pointBorderColor: nullMask.map((isNull) =>
              isNull ? "#6B7280" : color
            ),
            pointStyle: nullMask.map((isNull) =>
              isNull ? "crossRot" as const : "circle" as const
            ),
            pointRadius: nullMask.map((isNull) => (isNull ? 5 : 4)),
            pointHoverRadius: 7,
            pointHitRadius: 15,
            borderWidth: 2,
            fill: false,
            tension: 0,
            segment: {
              borderColor: (ctx: any) => {
                const p0Null = nullMask[ctx.p0DataIndex];
                const p1Null = nullMask[ctx.p1DataIndex];
                return (p0Null || p1Null) ? "#4B5563" : color;
              },
              borderDash: (ctx: any) => {
                const p0Null = nullMask[ctx.p0DataIndex];
                const p1Null = nullMask[ctx.p1DataIndex];
                return (p0Null || p1Null) ? [4, 4] : undefined;
              },
            },
            _nullMask: nullMask,
            _color: color,
          } as any;
        }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest" as const,
      intersect: false,
      axis: "xy" as const,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (item: any) => {
            const nullMask = item.dataset._nullMask;
            if (nullMask && nullMask[item.dataIndex]) {
              return `${item.dataset.label}: Dato non inserito`;
            }
            const val = item.raw as number;
            return `${item.dataset.label}: ${val.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 2 })} kg (1RM stimato)`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: true, color: "#374151" },
        ticks: { color: "#9ca3af", font: { size: 11 } },
        border: { color: "#374151" },
      },
      y: {
        beginAtZero: true,
        grid: { display: true, color: "#374151" },
        ticks: {
          color: "#9ca3af",
          font: { size: 11 },
          callback: (value: any) => `${Number(value).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`,
        },
        border: { color: "#374151" },
      },
    },
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <h4 className="text-sm font-semibold text-gray-100 mb-4">Top Set — 1RM Stimato</h4>
      <div className="flex gap-6 items-start">
        <div className="flex-1 min-w-0" style={{ height: 280 }}>
          <Line data={data} options={options} />
        </div>

        <div className="flex flex-col gap-1 justify-center flex-shrink-0 pl-4 border-l border-gray-700 w-[200px] overflow-hidden">
          {!isEmpty && (
            <>
            {sortedEntries.map((entry, idx) => {
              const color = exerciseColorMap[entry.exerciseId]
                || EXERCISE_COLORS[idx % EXERCISE_COLORS.length];
              const hasNulls = entry.oneRMPerWeek.some((v) => v === null);
              return (
                <div
                  key={entry.exerciseId}
                  className="flex items-center gap-1.5 text-[10px] px-2 py-0.5 text-gray-200"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="break-words">
                    {entry.exerciseName}
                    {hasNulls && <span className="text-gray-500 ml-1">*</span>}
                  </span>
                </div>
              );
            })}
            {sortedEntries.some((e) => e.oneRMPerWeek.some((v) => v === null)) && (
              <div className="text-[9px] text-gray-500 px-2 pt-2 border-t border-gray-700 mt-1">
                <span className="text-gray-500">✕</span> = dato non inserito
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {isEmpty && (
        <p className="text-red-400 text-xs mt-4 text-center">
          {hasSelection
            ? "Nessun esercizio con Top Set per i distretti selezionati"
            : "Seleziona un distretto muscolare per visualizzare il top set"}
        </p>
      )}
    </div>
  );
};
